import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  RefreshCw, LogOut, Package, ChefHat, CheckCircle, Clock, 
  User, Phone, FileText, Loader2, Bell, Ruler, Calendar,
  Volume2, VolumeX, Power, PowerOff, Lock, DollarSign, Plus, Trash2,
  ClipboardList, ShoppingBag, UserCheck, LockKeyhole, Unlock, X,
  Users, Crown, Star, Award, Mail, Hash, TrendingUp, Search, Ban, ShieldOff,
  XCircle, PauseCircle, PlayCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTIFICATION_SOUND = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3";

// Predefined staff names
const STAFF_NAMES = ["Sohel", "Salman", "Zafar", "Mota", "Kris"];

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("active");
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [togglingOrdering, setTogglingOrdering] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [saving, setSaving] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price_per_unit: "",
    unit: "lb",
    category: "Beef",
    available: true
  });
  
  // Staff name for signatures
  const [staffName, setStaffName] = useState(localStorage.getItem("staffName") || "");
  const [showStaffNameDialog, setShowStaffNameDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isOtherStaff, setIsOtherStaff] = useState(false);
  const [customStaffName, setCustomStaffName] = useState("");
  
  // Kiosk Lock Screen
  const [isLocked, setIsLocked] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [masterName, setMasterName] = useState("Master Admin");
  
  const audioRef = useRef(null);
  const chimeIntervalRef = useRef(null);
  const previousOrderCount = useRef(0);
  const isFirstLoad = useRef(true);

  // Play continuous chime for new orders
  const startContinuousChime = () => {
    if (chimeIntervalRef.current) return; // Already playing
    
    // Play immediately
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
    
    // Then repeat every 3 seconds
    chimeIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log("Audio play failed:", e));
      }
    }, 3000);
  };
  
  const stopContinuousChime = () => {
    if (chimeIntervalRef.current) {
      clearInterval(chimeIntervalRef.current);
      chimeIntervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 1.0;
    audioRef.current.load();
    
    return () => {
      stopContinuousChime();
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Check login and activate kiosk mode
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("staffLoggedIn");
    if (!isLoggedIn) {
      navigate("/staff");
      return;
    }
    
    // Activate Kiosk Mode on login
    const activateKioskMode = async () => {
      // Request fullscreen
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        }
      } catch (e) {
        console.log("Fullscreen not supported or denied");
      }
      
      // Request wake lock to prevent screen sleep
      try {
        if ('wakeLock' in navigator) {
          await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        console.log("Wake lock not supported");
      }
    };
    
    activateKioskMode();
    
    // Prevent back navigation
    const preventBack = (e) => {
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener('popstate', preventBack);
    
    // Prevent page close/refresh
    const preventClose = (e) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Use master password to exit.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', preventClose);
    
    return () => {
      window.removeEventListener('popstate', preventBack);
      window.removeEventListener('beforeunload', preventClose);
    };
  }, [navigate]);

  // Fetch master info
  const fetchMasterInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/info`);
      setMasterName(response.data.master_name);
    } catch (error) {
      console.error("Failed to fetch master info:", error);
    }
  }, []);

  // Fetch store settings
  const fetchStoreSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/store/settings`);
      setOrderingEnabled(response.data.ordering_enabled);
    } catch (error) {
      console.error("Failed to fetch store settings:", error);
    }
  }, []);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const newOrders = response.data;
      
      const activeOrders = newOrders.filter(o => ["placed", "received", "processing", "ready"].includes(o.status));
      const newOrderCount = activeOrders.filter(o => o.status === "placed").length;
      
      // Start continuous chime if there are unaccepted orders
      if (soundEnabled && newOrderCount > 0) {
        startContinuousChime();
        if (!isFirstLoad.current && newOrderCount > previousOrderCount.current) {
          toast.success("New order placed!", { duration: 5000 });
        }
      } else {
        stopContinuousChime();
      }
      
      previousOrderCount.current = newOrderCount;
      isFirstLoad.current = false;
      setOrders(newOrders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [soundEnabled]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/all`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  }, []);

  // Fetch customer stats
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/stats`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  }, []);

  // Toggle customer blacklist
  const toggleBlacklist = async (phone, currentStatus, customerName) => {
    try {
      await axios.put(`${API}/customers/blacklist`, {
        phone: phone,
        blacklisted: !currentStatus,
        reason: !currentStatus ? "Repeated no-shows" : null
      });
      
      if (!currentStatus) {
        toast.error(`${customerName} has been blacklisted`, { duration: 3000 });
      } else {
        toast.success(`${customerName} has been removed from blacklist`, { duration: 3000 });
      }
      
      fetchCustomers();
    } catch (error) {
      console.error("Failed to toggle blacklist:", error);
      toast.error("Failed to update blacklist status");
    }
  };

  const playNotificationSound = () => {
    if (audioRef.current && soundEnabled) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
  };

  const testSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => {
        toast.error("Please interact with the page first to enable sound");
      });
    }
  };

  // Initial load and polling
  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchCustomers();
    fetchStoreSettings();
    fetchMasterInfo();
    
    const interval = setInterval(() => {
      fetchOrders();
      fetchStoreSettings();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchOrders, fetchProducts, fetchCustomers, fetchStoreSettings, fetchMasterInfo]);

  // Stop chime when sound is disabled
  useEffect(() => {
    if (!soundEnabled) {
      stopContinuousChime();
    }
  }, [soundEnabled]);

  // Wake lock
  useEffect(() => {
    let wakeLock = null;
    
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.log('Wake Lock error:', err);
      }
    };
    
    requestWakeLock();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (wakeLock) wakeLock.release();
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchOrders(), fetchProducts(), fetchCustomers()]);
    setRefreshing(false);
    toast.success("Refreshed");
  };

  // Exit app - requires master password
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitPassword, setExitPassword] = useState("");
  const [exitError, setExitError] = useState("");

  const handleExitApp = async () => {
    if (!exitPassword) {
      setExitError("Please enter master password");
      return;
    }
    
    try {
      await axios.post(`${API}/master/unlock`, { password: exitPassword });
      // Exit fullscreen
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      }
      // Clear session and navigate away
      sessionStorage.removeItem("staffLoggedIn");
      window.removeEventListener('beforeunload', () => {});
      window.location.href = '/';
    } catch (error) {
      setExitError("Invalid password. Only master admin can exit.");
    }
  };

  // Lock screen function
  const lockScreen = () => {
    setIsLocked(true);
    toast.success(`Screen locked. Only ${masterName} can unlock.`);
  };

  // Unlock screen function - ONLY master password works
  const handleUnlock = async () => {
    if (!unlockPassword) {
      setUnlockError("Please enter password");
      return;
    }
    
    try {
      // Verify with MASTER unlock endpoint (separate from staff login)
      const response = await axios.post(`${API}/master/unlock`, { password: unlockPassword });
      setIsLocked(false);
      setShowUnlockDialog(false);
      setUnlockPassword("");
      setUnlockError("");
      toast.success(response.data.message);
    } catch (error) {
      setUnlockError("Invalid password. Only the master admin can unlock.");
    }
  };

  // Staff name required for order actions
  const requireStaffName = (action) => {
    if (!staffName) {
      setPendingAction(action);
      setShowStaffNameDialog(true);
      return false;
    }
    return true;
  };

  const saveStaffName = () => {
    if (!staffName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    localStorage.setItem("staffName", staffName);
    setShowStaffNameDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const doUpdate = async () => {
      setUpdatingOrderId(orderId);
      try {
        await axios.put(`${API}/orders/${orderId}/status`, { 
          status: newStatus,
          staff_name: staffName 
        });
        toast.success(`Order #${orderId} updated to ${newStatus}`);
        fetchOrders();
      } catch (error) {
        console.error("Failed to update order:", error);
        toast.error("Failed to update order status");
      } finally {
        setUpdatingOrderId(null);
      }
    };

    if (requireStaffName(doUpdate)) {
      doUpdate();
    }
  };

  const handleCompletionToggle = async (orderId, currentStatus) => {
    if (currentStatus === "ready") {
      const doComplete = async () => {
        setUpdatingOrderId(orderId);
        try {
          await axios.put(`${API}/orders/${orderId}/status`, { 
            status: "completed",
            staff_name: staffName 
          });
          toast.success(`Order #${orderId} completed`);
          fetchOrders();
        } catch (error) {
          console.error("Failed to complete order:", error);
          toast.error("Failed to complete order");
        } finally {
          setUpdatingOrderId(null);
        }
      };

      if (requireStaffName(doComplete)) {
        doComplete();
      }
    }
  };

  const handleToggleOrdering = async () => {
    if (!password) {
      toast.error("Please enter password");
      return;
    }
    
    setTogglingOrdering(true);
    try {
      const response = await axios.post(`${API}/store/toggle-ordering`, { password });
      setOrderingEnabled(response.data.ordering_enabled);
      setShowPasswordDialog(false);
      setPassword("");
      toast.success(response.data.ordering_enabled ? "Ordering ENABLED" : "Ordering DISABLED");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setTogglingOrdering(false);
    }
  };

  // Product management functions
  const updateProductPrice = async (productId, newPrice) => {
    setSaving(productId);
    try {
      const product = products.find(p => p.product_id === productId);
      await axios.put(`${API}/products/${productId}`, {
        ...product,
        price_per_unit: parseFloat(newPrice)
      });
      toast.success("Price updated");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update price");
    } finally {
      setSaving(null);
    }
  };

  const toggleProductAvailability = async (productId, currentStatus) => {
    setSaving(productId);
    try {
      const product = products.find(p => p.product_id === productId);
      await axios.put(`${API}/products/${productId}`, {
        ...product,
        available: !currentStatus
      });
      toast.success(currentStatus ? "Item discontinued" : "Item now available");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update item");
    } finally {
      setSaving(null);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    setSaving(productId);
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setSaving(null);
    }
  };

  const addProduct = async () => {
    if (!newProduct.name || !newProduct.price_per_unit) {
      toast.error("Please fill in name and price");
      return;
    }
    setSaving("new");
    try {
      await axios.post(`${API}/products`, {
        ...newProduct,
        price_per_unit: parseFloat(newProduct.price_per_unit),
        image_url: ""
      });
      toast.success("Product added");
      setShowAddDialog(false);
      setNewProduct({ name: "", description: "", price_per_unit: "", unit: "lb", category: "Beef", available: true });
      fetchProducts();
    } catch (error) {
      toast.error("Failed to add product");
    } finally {
      setSaving(null);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      placed: { icon: <Package className="w-5 h-5" />, label: "NEW ORDER", color: "bg-blue-600", textColor: "text-white" },
      received: { icon: <CheckCircle className="w-5 h-5" />, label: "CONFIRMED", color: "bg-[#A61B1B]", textColor: "text-white" },
      processing: { icon: <ChefHat className="w-5 h-5" />, label: "PREPARING", color: "bg-[#E67E22]", textColor: "text-white" },
      ready: { icon: <CheckCircle className="w-5 h-5" />, label: "READY", color: "bg-[#4A7C59]", textColor: "text-white" },
      completed: { icon: <CheckCircle className="w-5 h-5" />, label: "DONE", color: "bg-gray-400", textColor: "text-white" },
      delayed: { icon: <PauseCircle className="w-5 h-5" />, label: "DELAYED", color: "bg-orange-500", textColor: "text-white" },
      cancelled: { icon: <XCircle className="w-5 h-5" />, label: "CANCELLED", color: "bg-red-800", textColor: "text-white" },
    };
    return configs[status] || configs.placed;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === "active") return ["placed", "received", "processing", "ready"].includes(order.status);
    if (filter === "completed") return order.status === "completed";
    if (filter === "delayed") return order.status === "delayed";
    if (filter === "cancelled") return order.status === "cancelled";
    return true;
  });

  const orderCounts = {
    placed: orders.filter(o => o.status === "placed").length,
    received: orders.filter(o => o.status === "received").length,
    processing: orders.filter(o => o.status === "processing").length,
    ready: orders.filter(o => o.status === "ready").length,
    delayed: orders.filter(o => o.status === "delayed").length,
    cancelled: orders.filter(o => o.status === "cancelled").length
  };

  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) acc[product.category] = [];
    acc[product.category].push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="staff-dashboard">
      {/* KIOSK LOCK SCREEN OVERLAY */}
      {isLocked && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center" data-testid="lock-screen">
          <div className="text-center">
            <div className="w-32 h-32 bg-[#A61B1B] rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
              <LockKeyhole className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Screen Locked
            </h1>
            <p className="text-xl text-gray-400 mb-2">Market Place on Oakton</p>
            <p className="text-lg text-gray-500 mb-8">Only <span className="text-[#A61B1B] font-bold">{masterName}</span> can unlock</p>
            
            <Button
              onClick={() => setShowUnlockDialog(true)}
              size="lg"
              className="bg-[#A61B1B] hover:bg-[#8B1515] text-white text-xl px-12 py-6"
              data-testid="unlock-btn"
            >
              <Unlock className="w-6 h-6 mr-3" />
              Tap to Unlock
            </Button>
            
            <div className="mt-12 text-gray-600 text-sm">
              <p>Orders are still being received in the background</p>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Dialog - Placed outside lock screen for proper z-index */}
      {isLocked && showUnlockDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowUnlockDialog(false)} />
          <div className="relative bg-[#1A1A1A] text-white border border-gray-700 max-w-md w-full mx-4 p-6 rounded-lg shadow-xl">
            <button 
              onClick={() => { setShowUnlockDialog(false); setUnlockPassword(""); setUnlockError(""); }}
              className="absolute right-4 top-4 text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-2xl font-semibold mb-2">
              <Unlock className="w-7 h-7 text-[#A61B1B]" /> Unlock Screen
            </div>
            <p className="text-gray-400 text-lg mb-4">Enter master password to unlock</p>
            <div className="space-y-4">
              <div>
                <Label className="text-lg">Master Admin: <span className="text-[#A61B1B] font-bold">{masterName}</span></Label>
                <Input 
                  type="password" 
                  value={unlockPassword} 
                  onChange={(e) => { setUnlockPassword(e.target.value); setUnlockError(""); }}
                  placeholder="Enter password"
                  className="mt-3 bg-[#2A2A2A] border-gray-600 text-white text-lg py-6"
                  onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                  autoFocus
                  data-testid="unlock-password"
                />
                {unlockError && <p className="text-red-500 mt-2">{unlockError}</p>}
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => { setShowUnlockDialog(false); setUnlockPassword(""); setUnlockError(""); }} 
                  variant="outline" 
                  className="flex-1 border-gray-600 text-white text-lg py-6"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUnlock}
                  className="flex-1 bg-[#1A5E1F] hover:bg-[#155219] text-lg py-6"
                  data-testid="unlock-submit"
                >
                  <Unlock className="w-5 h-5 mr-2" /> Unlock
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-50 border-b-4 border-[#A61B1B]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://www.hmsusa.org/img/logo.png" 
              alt="HMS" 
              className="h-10 w-auto cursor-pointer" 
              onClick={(e) => {
                // Triple click to open exit dialog
                if (e.detail === 3) {
                  setShowExitDialog(true);
                }
              }}
              title="Triple-click for admin exit"
            />
            <div>
              <h1 className="text-xl font-[800]" style={{ fontFamily: 'Fraunces, serif' }}>
                Market Place on Oakton
              </h1>
              <p 
                className="text-xs text-gray-400 cursor-pointer hover:text-white transition-colors"
                onClick={() => setShowStaffNameDialog(true)}
              >
                Staff Portal • <span className="text-green-400 font-bold">{staffName || "Tap to select name"}</span>
              </p>
            </div>
          </div>
          
          {/* Status Indicators */}
          <div className="flex items-center gap-3">
            {orderCounts.placed > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-full animate-pulse">
                <Bell className="w-5 h-5" />
                <span className="font-bold text-lg">{orderCounts.placed} NEW</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#A61B1B] rounded">
              <CheckCircle className="w-4 h-4" />
              <span className="font-bold">{orderCounts.received}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#E67E22] rounded">
              <ChefHat className="w-4 h-4" />
              <span className="font-bold">{orderCounts.processing}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#4A7C59] rounded">
              <CheckCircle className="w-4 h-4" />
              <span className="font-bold">{orderCounts.ready}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Sound Toggle */}
            <Button
              onClick={() => { setSoundEnabled(!soundEnabled); if (!soundEnabled) testSound(); }}
              variant="outline"
              size="sm"
              className={`border-2 ${soundEnabled ? 'border-green-500 text-green-500' : 'border-gray-500 text-gray-500'} bg-transparent`}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>

            {/* Ordering Toggle */}
            <Button
              onClick={() => setShowPasswordDialog(true)}
              size="sm"
              style={{ backgroundColor: orderingEnabled ? '#166534' : '#991b1b', borderColor: orderingEnabled ? '#166534' : '#991b1b' }}
              className="text-white font-bold border-2"
              data-testid="ordering-toggle"
            >
              {orderingEnabled ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
              <span className="ml-1 font-bold">{orderingEnabled ? 'ON' : 'OFF'}</span>
            </Button>
            
            <Button onClick={handleRefresh} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-black bg-transparent" disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Ordering Disabled Banner */}
        {!orderingEnabled && (
          <div className="bg-red-600 text-white py-2 px-4 text-center font-bold">
            ORDERING IS CURRENTLY DISABLED - Customers cannot place new orders
          </div>
        )}
      </header>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="bg-[#2A2A2A] px-4 py-2 flex items-center justify-between">
          <TabsList className="bg-[#1A1A1A]">
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white">
              <ClipboardList className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#1A5E1F] data-[state=active]:text-white">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Products & Prices
            </TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-[#E67E22] data-[state=active]:text-white" data-testid="customers-tab">
              <Users className="w-4 h-4 mr-2" />
              Customers ({customers.length})
            </TabsTrigger>
          </TabsList>
          
          {activeTab === "orders" && (
            <div className="flex gap-2">
              <Button onClick={() => setFilter("active")} variant={filter === "active" ? "default" : "outline"} size="sm"
                className={filter === "active" ? "bg-[#A61B1B]" : "bg-transparent text-white border-gray-600"}>
                Active Orders
              </Button>
              <Button onClick={() => setFilter("delayed")} variant={filter === "delayed" ? "default" : "outline"} size="sm"
                className={filter === "delayed" ? "bg-[#E67E22]" : "bg-transparent text-white border-gray-600"}>
                Delayed
              </Button>
              <Button onClick={() => setFilter("completed")} variant={filter === "completed" ? "default" : "outline"} size="sm"
                className={filter === "completed" ? "bg-gray-600" : "bg-transparent text-white border-gray-600"}>
                Completed
              </Button>
              <Button onClick={() => setFilter("cancelled")} variant={filter === "cancelled" ? "default" : "outline"} size="sm"
                className={filter === "cancelled" ? "bg-red-800" : "bg-transparent text-white border-gray-600"}>
                Cancelled
              </Button>
            </div>
          )}
          
          {activeTab === "products" && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#1A5E1F] hover:bg-[#155219]">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Product</DialogTitle>
                  <DialogDescription>Add a new meat product to your catalog</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} placeholder="e.g., T-Bone Steak" className="mt-1" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={newProduct.description} onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} placeholder="e.g., Premium Zabiha Halal cut" className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price per lb *</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="number" step="0.01" value={newProduct.price_per_unit} onChange={(e) => setNewProduct({...newProduct, price_per_unit: e.target.value})} placeholder="0.00" className="pl-8" />
                      </div>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Beef">Beef</SelectItem>
                          <SelectItem value="Poultry">Poultry</SelectItem>
                          <SelectItem value="Lamb">Lamb</SelectItem>
                          <SelectItem value="Goat">Goat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={addProduct} disabled={saving === "new"} className="w-full bg-[#1A5E1F] hover:bg-[#155219]">
                    {saving === "new" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Product
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          <p className="text-gray-400 text-sm">
            Auto-refresh every 5s • {new Date().toLocaleTimeString()}
          </p>
        </div>

        {/* Orders Tab */}
        <TabsContent value="orders" className="p-4 mt-0">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-20">
              <Bell className="w-20 h-20 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-400">No orders</h3>
              <p className="text-gray-500">New orders will appear here with a sound alert</p>
              <Button onClick={testSound} variant="outline" className="mt-4 text-white border-gray-600">
                <Volume2 className="w-4 h-4 mr-2" /> Test Sound
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredOrders.map(order => {
                const statusConfig = getStatusConfig(order.status);
                const isUpdating = updatingOrderId === order.order_id;
                
                return (
                  <div key={order.order_id} className={`bg-white rounded-lg overflow-hidden shadow-xl ${order.status === "placed" ? "ring-4 ring-blue-600 animate-pulse" : ""}`} data-testid={`order-${order.order_id}`}>
                    {/* Status Header */}
                    <div className={`${statusConfig.color} ${statusConfig.textColor} px-4 py-3 flex items-center justify-between`}>
                      <div className="flex items-center gap-2">
                        {statusConfig.icon}
                        <span className="font-bold">{statusConfig.label}</span>
                      </div>
                      <span className="text-2xl font-[800]" style={{ fontFamily: 'Fraunces, serif' }}>#{order.order_id}</span>
                    </div>

                    <div className="p-4">
                      {/* Customer */}
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <div className="flex items-center gap-2 text-lg font-bold">
                          <User className="w-5 h-5 text-gray-400" />
                          {order.customer_name}
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Phone className="w-4 h-4" />
                          {order.customer_phone}
                        </div>
                      </div>

                      {/* Pickup Date */}
                      {order.pickup_date && (
                        <div className="mb-3 p-2 bg-[#E6F4EA] rounded flex items-center gap-2 text-[#1A5E1F] font-bold">
                          <Calendar className="w-4 h-4" />
                          Pickup: {new Date(order.pickup_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                      )}

                      {/* Items */}
                      <div className="mb-3">
                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Items</p>
                        {order.items.map((item, idx) => (
                          <div key={idx} className="bg-gray-50 p-2 rounded mb-1">
                            <div className="flex justify-between font-medium">
                              <div className="flex items-center gap-2">
                                <span>{item.product_name}</span>
                                {item.sku_code && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-mono">#{item.sku_code}</span>
                                )}
                              </div>
                              <span className="text-[#A61B1B] font-bold">{item.quantity} {item.unit}</span>
                            </div>
                            {item.brand && <p className="text-xs text-gray-500">{item.brand}</p>}
                            {item.piece_size && item.piece_size !== "N/A" && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Ruler className="w-3 h-3" /> Size: {item.piece_size}
                              </div>
                            )}
                            {item.notes && <p className="text-xs text-[#E67E22] italic mt-1">"{item.notes}"</p>}
                          </div>
                        ))}
                      </div>

                      {/* Special Instructions */}
                      {order.special_instructions && (
                        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-300 rounded text-sm">
                          <FileText className="w-4 h-4 text-yellow-600 inline mr-1" />
                          {order.special_instructions}
                        </div>
                      )}

                      {/* Staff Signatures */}
                      {(order.accepted_by || order.prepared_by || order.picked_up_by) && (
                        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs space-y-1">
                          {order.accepted_by && <p><span className="font-bold">Accepted:</span> {order.accepted_by}</p>}
                          {order.prepared_by && <p><span className="font-bold">Prepared:</span> {order.prepared_by}</p>}
                          {order.picked_up_by && <p><span className="font-bold">Pickup:</span> {order.picked_up_by}</p>}
                        </div>
                      )}

                      {/* Time */}
                      <div className="text-xs text-gray-400 mb-3">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {new Date(order.created_at).toLocaleString()}
                      </div>

                      {/* Actions */}
                      {order.status !== "completed" && order.status !== "cancelled" && (
                        <div className="space-y-2">
                          {order.status === "placed" && (
                            <>
                              <Button onClick={() => updateOrderStatus(order.order_id, "received")} disabled={isUpdating}
                                className="w-full bg-[#A61B1B] hover:bg-[#8B1717] text-white font-bold py-4 text-lg">
                                {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5 mr-2" /> CONFIRM ORDER</>}
                              </Button>
                              <div className="flex gap-2">
                                <Button onClick={() => updateOrderStatus(order.order_id, "delayed")} disabled={isUpdating}
                                  variant="outline" className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-50 font-bold py-2">
                                  <PauseCircle className="w-4 h-4 mr-1" /> DELAY
                                </Button>
                                <Button onClick={() => updateOrderStatus(order.order_id, "cancelled")} disabled={isUpdating}
                                  variant="outline" className="flex-1 border-red-500 text-red-500 hover:bg-red-50 font-bold py-2">
                                  <XCircle className="w-4 h-4 mr-1" /> CANCEL
                                </Button>
                              </div>
                            </>
                          )}
                          {order.status === "received" && (
                            <>
                              <Button onClick={() => updateOrderStatus(order.order_id, "processing")} disabled={isUpdating}
                                className="w-full bg-[#E67E22] hover:bg-[#D97706] text-white font-bold py-4 text-lg">
                                {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ChefHat className="w-5 h-5 mr-2" /> START PREPARING</>}
                              </Button>
                              <div className="flex gap-2">
                                <Button onClick={() => updateOrderStatus(order.order_id, "delayed")} disabled={isUpdating}
                                  variant="outline" className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-50 font-bold py-2">
                                  <PauseCircle className="w-4 h-4 mr-1" /> DELAY
                                </Button>
                                <Button onClick={() => updateOrderStatus(order.order_id, "cancelled")} disabled={isUpdating}
                                  variant="outline" className="flex-1 border-red-500 text-red-500 hover:bg-red-50 font-bold py-2">
                                  <XCircle className="w-4 h-4 mr-1" /> CANCEL
                                </Button>
                              </div>
                            </>
                          )}
                          {order.status === "processing" && (
                            <>
                              <Button onClick={() => updateOrderStatus(order.order_id, "ready")} disabled={isUpdating}
                                className="w-full bg-[#4A7C59] hover:bg-[#3D6B4A] text-white font-bold py-4 text-lg">
                                {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5 mr-2" /> MARK READY</>}
                              </Button>
                              <Button onClick={() => updateOrderStatus(order.order_id, "delayed")} disabled={isUpdating}
                                variant="outline" className="w-full border-orange-500 text-orange-500 hover:bg-orange-50 font-bold py-2">
                                <PauseCircle className="w-4 h-4 mr-1" /> DELAY ORDER
                              </Button>
                            </>
                          )}
                          {order.status === "ready" && (
                            <div className="flex items-center justify-between p-4 bg-[#1A5E1F] border-2 border-[#0F4012] rounded-lg">
                              <Label className="text-lg font-bold text-white">Order Picked Up?</Label>
                              <Switch checked={false} onCheckedChange={() => handleCompletionToggle(order.order_id, order.status)} disabled={isUpdating} className="scale-150 data-[state=unchecked]:bg-gray-400" />
                            </div>
                          )}
                          {order.status === "delayed" && (
                            <div className="space-y-2">
                              <Button onClick={() => updateOrderStatus(order.order_id, "received")} disabled={isUpdating}
                                className="w-full bg-[#1A5E1F] hover:bg-[#155219] text-white font-bold py-4 text-lg">
                                <PlayCircle className="w-5 h-5 mr-2" /> RESUME ORDER
                              </Button>
                              <Button onClick={() => updateOrderStatus(order.order_id, "cancelled")} disabled={isUpdating}
                                variant="outline" className="w-full border-red-500 text-red-500 hover:bg-red-50 font-bold py-2">
                                <XCircle className="w-4 h-4 mr-1" /> CANCEL ORDER
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Cancelled order - allow restore */}
                      {order.status === "cancelled" && (
                        <Button onClick={() => updateOrderStatus(order.order_id, "placed")} disabled={isUpdating}
                          variant="outline" className="w-full border-green-500 text-green-500 hover:bg-green-50 font-bold py-2">
                          <PlayCircle className="w-4 h-4 mr-1" /> RESTORE ORDER
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="p-4 mt-0">
          <div className="max-w-4xl mx-auto space-y-6">
            {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
              <div key={category} className="bg-white border-2 border-[#DCD7CC] rounded-lg overflow-hidden">
                <div className="bg-[#1A1A1A] text-white px-4 py-3 flex justify-between items-center">
                  <h2 className="text-lg font-[700]" style={{ fontFamily: 'Fraunces, serif' }}>{category}</h2>
                  <span className="text-sm text-gray-400">{categoryProducts.length} items</span>
                </div>
                <div className="divide-y divide-[#DCD7CC]">
                  {categoryProducts.map(product => (
                    <div key={product.product_id} className={`p-4 ${!product.available ? 'bg-gray-100 opacity-60' : ''}`} data-testid={`product-row-${product.product_id}`}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <h3 className="font-[600] text-[#1A1A1A]">{product.name}</h3>
                            {product.sku_code && <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-mono">#{product.sku_code}</span>}
                            {product.brand && <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">{product.brand}</span>}
                            {!product.available && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Discontinued</span>}
                          </div>
                          <p className="text-sm text-[#8A8A8A]">{product.description}</p>
                        </div>
                        
                        {/* Price */}
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input type="number" step="0.01" defaultValue={product.price_per_unit}
                              onBlur={(e) => { if (parseFloat(e.target.value) !== product.price_per_unit) updateProductPrice(product.product_id, e.target.value); }}
                              className="w-24 pl-7 text-right font-bold" data-testid={`price-${product.product_id}`} />
                          </div>
                          <span className="text-sm text-[#8A8A8A]">/lb</span>
                        </div>

                        {/* Toggle */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-[#8A8A8A]">{product.available ? 'Available' : 'Discontinued'}</Label>
                          <Switch 
                            checked={product.available} 
                            onCheckedChange={() => toggleProductAvailability(product.product_id, product.available)}
                            disabled={saving === product.product_id} 
                            data-testid={`toggle-${product.product_id}`}
                            className="data-[state=checked]:bg-green-800 data-[state=unchecked]:bg-red-800"
                          />
                        </div>

                        {/* Delete */}
                        <Button variant="ghost" size="sm" onClick={() => deleteProduct(product.product_id)} disabled={saving === product.product_id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${product.product_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="p-4 mt-0">
          <div className="max-w-5xl mx-auto">
            {/* Search and Stats Header */}
            <div className="bg-white border-2 border-[#DCD7CC] rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-[700] text-[#1A1A1A]" style={{ fontFamily: 'Fraunces, serif' }}>
                    Customer Library
                  </h2>
                  <Badge className="bg-[#E67E22] text-white text-lg px-3 py-1">
                    {customers.length} Customers
                  </Badge>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or phone..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-10 w-64 border-2 border-[#DCD7CC]"
                    data-testid="customer-search"
                  />
                </div>
              </div>
              
              {/* Tier Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#DCD7CC]">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-[#FFD700]" />
                  <span className="text-sm"><strong>VIP</strong> (20+ orders)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#C0C0C0]" />
                  <span className="text-sm"><strong>Regular</strong> (10-19 orders)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#CD7F32]" />
                  <span className="text-sm"><strong>Returning</strong> (5-9 orders)</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#4A7C59]" />
                  <span className="text-sm"><strong>New</strong> (1-4 orders)</span>
                </div>
              </div>
            </div>

            {/* Customer List */}
            <div className="space-y-3">
              {customers
                .filter(c => {
                  if (!customerSearch) return true;
                  const search = customerSearch.toLowerCase();
                  return c.customer_name?.toLowerCase().includes(search) || 
                         c.customer_phone?.includes(search) ||
                         c.customer_email?.toLowerCase().includes(search);
                })
                .map((customer, index) => {
                  const tierIcon = customer.tier === 'VIP' ? <Crown className="w-5 h-5" /> :
                                   customer.tier === 'Regular' ? <Star className="w-5 h-5" /> :
                                   customer.tier === 'Returning' ? <Award className="w-5 h-5" /> :
                                   <User className="w-5 h-5" />;
                  
                  return (
                    <div 
                      key={customer._id || index}
                      className={`bg-white border-2 rounded-lg p-4 hover:border-[#E67E22] transition-colors ${customer.blacklisted ? 'border-red-500 bg-red-50' : 'border-[#DCD7CC]'}`}
                      data-testid={`customer-${customer.customer_phone}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Customer Info */}
                        <div className="flex items-center gap-4">
                          <div 
                            className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl ${customer.blacklisted ? 'bg-red-600' : ''}`}
                            style={{ backgroundColor: customer.blacklisted ? undefined : customer.tier_color }}
                          >
                            {customer.blacklisted ? <Ban className="w-7 h-7" /> : customer.total_orders}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`text-lg font-[700] ${customer.blacklisted ? 'text-red-700 line-through' : 'text-[#1A1A1A]'}`}>{customer.customer_name}</h3>
                              {customer.blacklisted ? (
                                <span className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded bg-red-100 text-red-700">
                                  <Ban className="w-3 h-3" />
                                  BLACKLISTED
                                </span>
                              ) : (
                                <span 
                                  className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded"
                                  style={{ backgroundColor: customer.tier_color + '20', color: customer.tier_color }}
                                >
                                  {tierIcon}
                                  {customer.tier}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#8A8A8A]">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {customer.customer_phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {customer.customer_email}
                              </span>
                            </div>
                            {customer.blacklisted && customer.blacklist_reason && (
                              <p className="text-xs text-red-600 mt-1 italic">Reason: {customer.blacklist_reason}</p>
                            )}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-[#A61B1B]">{customer.total_orders}</p>
                            <p className="text-[#8A8A8A]">Total Orders</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-[#1A5E1F]">{customer.total_items}</p>
                            <p className="text-[#8A8A8A]">Items Ordered</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-[#1A1A1A]">
                              {customer.first_order_date ? new Date(customer.first_order_date).toLocaleDateString() : 'N/A'}
                            </p>
                            <p className="text-[#8A8A8A]">First Order</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-[#1A1A1A]">
                              {customer.last_order_date ? new Date(customer.last_order_date).toLocaleDateString() : 'N/A'}
                            </p>
                            <p className="text-[#8A8A8A]">Last Order</p>
                          </div>
                          
                          {/* Blacklist Toggle */}
                          <div className="text-center border-l-2 border-gray-200 pl-6">
                            <Button
                              onClick={() => toggleBlacklist(customer.customer_phone, customer.blacklisted, customer.customer_name)}
                              variant={customer.blacklisted ? "default" : "outline"}
                              size="sm"
                              className={customer.blacklisted 
                                ? "bg-green-600 hover:bg-green-700 text-white" 
                                : "border-red-500 text-red-500 hover:bg-red-50"
                              }
                              data-testid={`blacklist-toggle-${customer.customer_phone}`}
                            >
                              {customer.blacklisted ? (
                                <><ShieldOff className="w-4 h-4 mr-1" /> Unblock</>
                              ) : (
                                <><Ban className="w-4 h-4 mr-1" /> Blacklist</>
                              )}
                            </Button>
                            <p className="text-[#8A8A8A] text-xs mt-1">No-Shows</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {customers.length === 0 && (
                <div className="bg-white border-2 border-[#DCD7CC] rounded-lg p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg text-[#8A8A8A]">No customers yet</p>
                  <p className="text-sm text-[#8A8A8A]">Customer data will appear here once orders are placed</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Staff Name Dialog */}
      <Dialog open={showStaffNameDialog} onOpenChange={(open) => {
        setShowStaffNameDialog(open);
        if (!open) {
          setIsOtherStaff(false);
          setCustomStaffName("");
        }
      }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <UserCheck className="w-6 h-6" /> Who's Working?
            </DialogTitle>
            <DialogDescription>Select your name to track order handling</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {!isOtherStaff ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {STAFF_NAMES.map((name) => (
                    <Button
                      key={name}
                      onClick={() => {
                        setStaffName(name);
                        localStorage.setItem("staffName", name);
                        setShowStaffNameDialog(false);
                        toast.success(`Welcome, ${name}!`);
                        if (pendingAction) {
                          pendingAction();
                          setPendingAction(null);
                        }
                      }}
                      variant={staffName === name ? "default" : "outline"}
                      className={`py-6 text-lg ${staffName === name ? 'bg-[#1A5E1F] hover:bg-[#155219]' : 'hover:bg-gray-100'}`}
                    >
                      {name}
                    </Button>
                  ))}
                </div>
                <Button
                  onClick={() => setIsOtherStaff(true)}
                  variant="outline"
                  className="w-full py-4 border-dashed border-2"
                >
                  Other (Not on list)
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Enter Your Name</Label>
                  <Input 
                    value={customStaffName} 
                    onChange={(e) => setCustomStaffName(e.target.value)} 
                    placeholder="Type your name"
                    className="mt-2" 
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customStaffName.trim()) {
                        setStaffName(customStaffName.trim());
                        localStorage.setItem("staffName", customStaffName.trim());
                        setShowStaffNameDialog(false);
                        setIsOtherStaff(false);
                        setCustomStaffName("");
                        toast.success(`Welcome, ${customStaffName.trim()}!`);
                        if (pendingAction) {
                          pendingAction();
                          setPendingAction(null);
                        }
                      }
                    }} 
                  />
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => setIsOtherStaff(false)} variant="outline" className="flex-1">Back</Button>
                  <Button 
                    onClick={() => {
                      if (customStaffName.trim()) {
                        setStaffName(customStaffName.trim());
                        localStorage.setItem("staffName", customStaffName.trim());
                        setShowStaffNameDialog(false);
                        setIsOtherStaff(false);
                        setCustomStaffName("");
                        toast.success(`Welcome, ${customStaffName.trim()}!`);
                        if (pendingAction) {
                          pendingAction();
                          setPendingAction(null);
                        }
                      }
                    }} 
                    className="flex-1 bg-[#1A5E1F] hover:bg-[#155219]"
                    disabled={!customStaffName.trim()}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-[#1A1A1A] text-white border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Lock className="w-6 h-6" /> {orderingEnabled ? "Disable Ordering?" : "Enable Ordering?"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {orderingEnabled ? "This will prevent customers from placing new orders." : "This will allow customers to place orders again."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Enter Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter staff password"
                className="mt-2 bg-[#2A2A2A] border-gray-600 text-white" onKeyDown={(e) => e.key === 'Enter' && handleToggleOrdering()} />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { setShowPasswordDialog(false); setPassword(""); }} variant="outline" className="flex-1 border-gray-600 text-white">Cancel</Button>
              <Button onClick={handleToggleOrdering} disabled={togglingOrdering}
                className={`flex-1 ${orderingEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {togglingOrdering ? <Loader2 className="w-4 h-4 animate-spin" /> : (orderingEnabled ? "DISABLE ORDERING" : "ENABLE ORDERING")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit App Dialog - Master Password Required */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="bg-[#1A1A1A] text-white border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-red-500">
              <LogOut className="w-6 h-6" /> Exit Kiosk Mode
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Only {masterName} can exit the app. Enter master password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Master Password</Label>
              <Input 
                type="password" 
                value={exitPassword} 
                onChange={(e) => { setExitPassword(e.target.value); setExitError(""); }} 
                placeholder="Enter master password"
                className="mt-2 bg-[#2A2A2A] border-gray-600 text-white" 
                onKeyDown={(e) => e.key === 'Enter' && handleExitApp()} 
              />
              {exitError && <p className="text-red-500 text-sm mt-2">{exitError}</p>}
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { setShowExitDialog(false); setExitPassword(""); setExitError(""); }} variant="outline" className="flex-1 border-gray-600 text-white">Cancel</Button>
              <Button onClick={handleExitApp} className="flex-1 bg-red-600 hover:bg-red-700">
                EXIT APP
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffDashboard;
